// HeadlineMetrics — FMV, strike, equity value, discount (F5.1-F5.3, F5.5).
//
// Stage 6e /polish: the hero FMV sits on the open page (no .card container)
// so the canonical 409A output reads as the page's primary signal. The
// supporting metrics below it also drop their container — typographic
// hierarchy and a quiet top rule do the grouping work. Same IBM Plex /
// navy / semantic palette; spacing and border emphasis only.

import type { HeadlineOutputs } from '@backsolve/shared-types';
import { usd, usdPrecise, pct } from '../../format/format';
import { Tooltip } from '../common/Tooltip';
import { useConvertibleAdjustedFmv } from '../../store/selectors';

function MetricLabel({ label, help }: { label: string; help: string }) {
  return (
    <span className="metric-label">
      {label}
      <Tooltip label={help} ariaLabel={`Help: ${label}`} />
    </span>
  );
}

export function HeadlineMetrics({ headline }: { headline: HeadlineOutputs }) {
  const convertedFmv = useConvertibleAdjustedFmv();
  return (
    <section className="panel-open headline-metrics">
      <div className="panel-hero metric">
        <MetricLabel
          label="Common FMV per Share"
          help="Fair market value of one common share in USD, after allocating total equity across the waterfall and applying DLOM. This is the 409A strike-price floor."
        />
        <span className="metric-value metric-value-hero">{usdPrecise(headline.commonFmvPerShare)}</span>
      </div>

      {convertedFmv && (
        <div className="metric mb-4">
          <MetricLabel
            label="If-Converted FMV (SAFEs/notes)"
            help="Preview only: the per-share common value if all convertibles were as-converted into the fully-diluted denominator. The canonical FMV above excludes convertibles; this is not used for the strike price."
          />
          <span className="metric-value">
            {usdPrecise(convertedFmv.adjustedFmvPerShare)}{' '}
            <span className="field-hint">(Δ {pct(convertedFmv.deltaPct)})</span>
          </span>
        </div>
      )}

      <div className="metric-grid-tight">
        <div className="metric">
          <MetricLabel
            label="Recommended Strike"
            help="The lowest permissible option strike: the FMV rounded up to the nearest cent (409A prohibits granting below FMV)."
          />
          <span className="metric-value">{usdPrecise(headline.recommendedStrikePrice)}</span>
        </div>
        <div className="metric">
          <MetricLabel
            label="Total Equity Value"
            help="Allocated equity value (V) used as the waterfall input. For backsolve this is solved from the anchor; otherwise it is the direct equity value entered."
          />
          <span className="metric-value">{usd(headline.totalEquityValue)}</span>
        </div>
        <div className="metric">
          <MetricLabel
            label="Common Value (pre-DLOM)"
            help="Sum allocated to common-share classes across all breakpoints, before the lack-of-marketability discount is applied. In USD."
          />
          <span className="metric-value">{usd(headline.commonValuePreDlom)}</span>
        </div>
        <div className="metric">
          <MetricLabel
            label="Common Value (post-DLOM)"
            help="Common value after the lack-of-marketability and minority discounts. Divided by fully-diluted shares to get FMV per share. In USD."
          />
          <span className="metric-value">{usd(headline.commonValuePostDlom)}</span>
        </div>
        <div className="metric">
          <MetricLabel
            label="Discount to Preferred"
            help="(Latest preferred price − common FMV) / latest preferred price, as a ratio. A common indicator of how illiquid/diluted common is vs. the most recent priced round."
          />
          <span className="metric-value">
            {headline.commonToPreferredDiscount !== null ? pct(headline.commonToPreferredDiscount) : 'n/a'}
          </span>
        </div>
        <div className="metric">
          <MetricLabel
            label="Safe-Harbor Window"
            help="Days during which the selected safe harbor (e.g. independent appraisal) is treated as valid for 409A. Refresh annually (365 days)."
          />
          <span className="metric-value">{headline.safeHarborWindowDays} days</span>
        </div>
      </div>
      <details className="how-this-works">
        <summary>How this works</summary>
        <div className="how-this-works-body">
          The engine allocates the total equity value across liquidation breakpoints
          (waterfall), sums the value landing on common classes, applies the chosen
          DLOM model, and divides by fully-diluted shares. The strike is the FMV
          rounded up to the nearest cent. See the Allocation and Breakpoint tables
          below for the per-tranche decomposition.
        </div>
      </details>
    </section>
  );
}
